import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { categories } from "@/mocks/videos";
import { useScrolled } from "@/hooks/useScrolled";

const navLinks = [
  { to: "/", label: "首页" },
  { to: "/creator", label: "创作者中心" },
  { to: "/about", label: "关于与帮助" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const scrolled = useScrolled(10);
  const [keyword, setKeyword] = useState("");
  const [catOpen, setCatOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = () => {
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 w-full border-b transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-background-200 shadow-sm"
          : "bg-background-50 border-transparent"
      }`}
    >
      <div className="h-16 px-4 md:px-6 flex items-center gap-4 md:gap-8">
        <Link to="/" className="flex items-center gap-2 shrink-0 group">
          <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center text-background-50 text-lg font-bold transition-transform group-hover:scale-105">
            <i className="ri-play-fill"></i>
          </span>
          <span className="text-2xl text-primary-600 font-brand leading-none">快手</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                isActive(link.to)
                  ? "text-primary-600 bg-primary-50"
                  : "text-foreground-600 hover:text-primary-600 hover:bg-background-100"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className="px-3 py-2 rounded-md text-sm font-medium text-foreground-600 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-1"
            >
              分类频道
              <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
            </button>
            {catOpen && (
              <div className="absolute top-full left-0 mt-2 w-[420px] bg-background-50 border border-background-200 rounded-lg p-2 grid grid-cols-2 gap-1 shadow-lg animate-fade-in">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                      <i className={cat.icon}></i>
                    </span>
                    <span className="text-sm text-foreground-800">{cat.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="hidden md:flex items-center flex-1 justify-end gap-3">
          <div className="relative w-full max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch();
              }}
              type="text"
              placeholder="搜索视频、作者或标签"
              className="w-full h-9 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
            />
          </div>
          <button
            type="button"
            onClick={submitSearch}
            className="h-9 px-4 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
          aria-label="菜单"
        >
          <i className={`text-2xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 space-y-4 animate-fade-in">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch();
              }}
              type="text"
              placeholder="搜索视频、作者或标签"
              className="w-full h-10 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm outline-none focus:border-primary-400"
            />
          </div>
          <div className="flex flex-col">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`py-2.5 px-2 rounded-md text-sm font-medium ${
                  isActive(link.to) ? "text-primary-600 bg-primary-50" : "text-foreground-700"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
          <div>
            <p className="text-xs text-foreground-400 mb-2 px-2">分类频道</p>
            <div className="grid grid-cols-3 gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="flex flex-col items-center gap-1 py-2 rounded-md bg-background-100 hover:bg-primary-50 transition-colors"
                >
                  <i className={`${cat.icon} text-primary-500 text-lg`}></i>
                  <span className="text-xs text-foreground-700">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}